import React, { useState } from "react";

import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";
import { CandidateAvatar } from "@modules/ServiceRequest/Components/CandidatesSection/CandidateAvatar";
import { VStack, HStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useCookies from "@Libs/Utils/hooks/useCookies";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { useLevelUpInfo } from "../../hooks/useLevelUpInfo";
import messages from "./messages";

const COOKIE_NAME = "identity_verification_remind_at";
const REMIND_AFTER_DAYS = 7;

const isCookieExpiredOrAbsent = (
  cookies: ReturnType<typeof useCookies>
): boolean => {
  const remindAt = cookies.get<string>(COOKIE_NAME);
  if (!remindAt) return true;
  const diffMs = Date.now() - new Date(remindAt).getTime();
  return diffMs >= REMIND_AFTER_DAYS * 24 * 60 * 60 * 1000;
};

export interface IIdentityVerificationModalProps {
  profilePictureUrl: string;
}

export const IdentityVerificationModal = ({
  profilePictureUrl,
}: IIdentityVerificationModalProps) => {
  const cookies = useCookies();
  const [isDismissed, setIsDismissed] = useState(false);
  const { currentUser, isSuccess: isCurrentUserSuccess } = useCurrentUser({
    includes: ["serviceProvider"],
  });
  const { levelUpInfo, isSuccess: isLevelUpInfoSuccess } = useLevelUpInfo();

  const handleRemindLater = () => {
    cookies.set(COOKIE_NAME, new Date().toISOString(), { path: "/" });
    setIsDismissed(true);
  };

  const notDismissedInLastWeek = isCookieExpiredOrAbsent(cookies) && !isDismissed;
  const userNotVerified = currentUser?.serviceProvider?.verified === false;
  const kycItem = levelUpInfo?.find((info) => info['key'] === 'KYC');
  const kycMissing = kycItem?.state === "MISSING";
  const kycOptional = kycItem?.optional === true;
  const isOpen =
    isCurrentUserSuccess &&
    isLevelUpInfoSuccess &&
    notDismissedInLastWeek &&
    userNotVerified &&
    kycMissing &&
    kycOptional;

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleRemindLater} closeButtonDisabled closeOnOverlayClick={false} closeOnEsc={false} bodyPadding="40px">
      <VStack spacing="24px" w="full" textAlign="center" pb="16px">
        <VStack spacing="8px" w="full">
          <Heading as="h3" fontSize="xl">
            <FormattedMessage {...messages["identityVerificationModal.title"]} />
          </Heading>
          <Text fontSize="md" color="gray.600">
            <FormattedMessage
              {...messages["identityVerificationModal.subtitle"]}
            />
          </Text>
        </VStack>

        <CandidateAvatar url={profilePictureUrl} size="lg" isSuperTalent={false} isVerified={true} />

        <Text fontSize="md" color="gray.600">
          <FormattedMessage
            {...messages["identityVerificationModal.reminder"]}
          />
        </Text>

        <HStack spacing="12px" w="full" justify="center">
          <Button
            variant="outline"
            colorScheme="seaGreen"
            onClick={handleRemindLater}
          >
            <FormattedMessage
              {...messages["identityVerificationModal.remindMeLater"]}
            />
          </Button>
          <Button colorScheme="seaGreen" as="a" href="/level_up">
            <FormattedMessage
              {...messages["identityVerificationModal.verifyMe"]}
            />
          </Button>
        </HStack>
      </VStack>
    </ResponsiveModal>
  );
};
